import BookingWhenList from '@/features/reservations/components/BookingWhenList';
import EditGuestFields from '@/features/reservations/components/EditGuestFields';
import { useBookingLimits } from '@/features/reservations/hooks/useBookingLimits';
import { useUpdateReservation } from '@/features/reservations/hooks/useReservationMutations';
import { editBody, editProblem, editValues } from '@/features/reservations/model/reservation-form';
import { useT } from '@/i18n';
import { useContractForm } from '@/lib/forms/useContractForm';
import type { Reservation, ReservationUpdateBody } from '@restaurant/api-client';
import { ReservationUpdateSchema } from '@restaurant/api-contracts';
import {
  BlockTitle,
  f7,
  Link as F7Link,
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  Preloader,
} from 'framework7-react';
import { useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';

interface Props {
  reservation: Reservation;
  onClose: () => void;
}

export default function EditReservationForm({ reservation, onClose }: Props) {
  const t = useT();
  const update = useUpdateReservation();
  const saved = useMemo(() => editValues(reservation), [reservation]);
  const form = useContractForm(ReservationUpdateSchema, {
    defaultValues: saved,
    values: saved,
    toBody: (values) => editBody(values, reservation),
  });
  const { control } = form;
  const { field: date } = useController({ control, name: 'date' });
  const { field: time } = useController({ control, name: 'time' });
  const { field: partySize } = useController({ control, name: 'partySize' });
  const { field: duration } = useController({ control, name: 'duration' });
  // Validity of the phone reported by PhonePicker (5–20 chars + a real number).
  const [phoneValid, setPhoneValid] = useState(true);
  const submitting = useRef(false);

  // Only meaningful once the slot actually moved: the reservation already sits in
  // its current slot, so a same-slot capacity check would count itself and
  // wrongly report "full".
  const slotChanged =
    date.value !== saved.date || time.value !== saved.time || partySize.value !== saved.partySize;
  // Party-size limit only. Staff pick the date freely (no advance-window cap) -
  // they book on the guest's behalf and routinely go further out than the
  // self-serve online horizon; the staff backend path is unrestricted too.
  const limits = useBookingLimits(date.value, time.value, partySize.value, slotChanged);

  const submit = (data: ReservationUpdateBody) => {
    if (submitting.current) return;
    submitting.current = true;
    update.mutate(
      { reservationId: reservation.id, data },
      {
        onSuccess: () => {
          f7.toast.show({ text: t.editReservation.updated, closeTimeout: 2000 });
          onClose();
        },
        onError: () => f7.dialog.alert(t.editReservation.updateFailed),
        onSettled: () => {
          submitting.current = false;
        },
      },
    );
  };

  const save = () => {
    const problem = editProblem(form.getValues(), phoneValid);
    if (problem) {
      f7.dialog.alert(t.createReservation[problem]);
      return;
    }
    void form.handleSubmit(
      (data) => {
        if (Object.keys(data).length === 0) {
          onClose();
          return;
        }
        // Raising the party size above the configured limit requires an explicit
        // confirm - same gate as the create form. Only prompts when the size is
        // actually being changed to an over-limit value, not on unrelated edits.
        const partyOverLimit = data.partySize !== undefined && data.partySize > limits.maxPartySize;
        limits.confirmThen(() => submit(data), partyOverLimit);
      },
      () => f7.dialog.alert(t.editReservation.updateFailed),
    )();
  };

  return (
    <Page>
      <Navbar>
        <NavLeft>
          <F7Link popupClose>{t.cancel}</F7Link>
        </NavLeft>
        <NavTitle>{t.editReservation.title}</NavTitle>
        <NavRight>
          {update.isPending ? (
            <Preloader size={20} />
          ) : (
            <F7Link onClick={save}>{t.editReservation.save}</F7Link>
          )}
        </NavRight>
      </Navbar>

      <BlockTitle>{t.editReservation.sectionWhen}</BlockTitle>
      <BookingWhenList
        date={date}
        time={time}
        partySize={partySize}
        duration={duration}
        maxPartySize={limits.maxPartySize}
        partyOverLimit={limits.partyOverLimit}
        showSlotFull={limits.showSlotFull}
      />

      <EditGuestFields control={control} savedPhone={saved.phone} onPhoneValid={setPhoneValid} />
    </Page>
  );
}
